// AdmitCardsPanel — build a day-by-day exam datesheet for a class (exam
// name, session start/end date, subject + time per day), then print an
// admit card (one per page) for every selected student in that class.
// Datesheets are saved on the backend so they can be reopened and reprinted
// later; printing itself works off the form's current draft, so an admin
// can print without saving if they're in a hurry.
import { useEffect, useState } from 'react';
import { Printer, AlertTriangle, Plus, Trash2, Save } from 'lucide-react';
import { getClasses, getIdCards, getDatesheets, getDatesheet, saveDatesheet } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { AdmitCard, AdmitCardSheet } from './AdmitCard';

const inputCls = 'border border-line rounded-s px-3 py-2 text-sm bg-paper-raised text-ink focus:outline-none focus:ring-2 focus:ring-transit';
const BLANK_ENTRY = { exam_date: '', subject: '', start_time: '', end_time: '' };

export default function AdmitCardsPanel({ onOpenStudent }) {
  const [classes,     setClasses]     = useState([]);
  const [classId,     setClassId]     = useState('');
  const [datesheets,  setDatesheets]  = useState([]);
  const [datesheetId, setDatesheetId] = useState('');

  const [examName,  setExamName]  = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate,   setEndDate]   = useState('');
  const [entries,   setEntries]   = useState([{ ...BLANK_ENTRY }]);

  const [roster,   setRoster]   = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [print,    setPrint]    = useState(false);
  const [msg,      setMsg]      = useState('');
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    getClasses().then(cs => { setClasses(cs); if (cs.length) setClassId(String(cs[0].id)); })
      .catch(err => setMsg('Error: ' + (err.response?.data?.error || err.message)));
  }, []);

  useEffect(() => {
    if (!classId) return;
    setMsg(''); setDatesheetId(''); resetForm();
    getIdCards({ class_id: classId })
      .then(d => { setRoster(d); setSelected(new Set(d.students.map(s => s.id))); })
      .catch(err => setMsg('Error: ' + (err.response?.data?.error || err.message)));
    getDatesheets(classId).then(setDatesheets)
      .catch(err => setMsg('Error: ' + (err.response?.data?.error || err.message)));
  }, [classId]);

  function resetForm() {
    setExamName(''); setStartDate(''); setEndDate(''); setEntries([{ ...BLANK_ENTRY }]);
  }

  function pickDatesheet(id) {
    setDatesheetId(id);
    if (!id) { resetForm(); return; }
    getDatesheet(id).then(d => {
      setExamName(d.exam_name);
      setStartDate(d.start_date || '');
      setEndDate(d.end_date || '');
      setEntries(d.entries.length ? d.entries.map(e => ({
        exam_date: e.exam_date, subject: e.subject, start_time: e.start_time || '', end_time: e.end_time || '',
      })) : [{ ...BLANK_ENTRY }]);
    }).catch(err => setMsg('Error: ' + (err.response?.data?.error || err.message)));
  }

  function updateEntry(i, field, value) {
    setEntries(prev => prev.map((e, idx) => idx === i ? { ...e, [field]: value } : e));
  }
  function addEntry() {
    const last = entries[entries.length - 1];
    setEntries(prev => [...prev, { ...BLANK_ENTRY, exam_date: last?.exam_date || startDate || '' }]);
  }
  function removeEntry(i) {
    setEntries(prev => prev.filter((_, idx) => idx !== i));
  }

  function toggle(id) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }

  const validEntries = entries.filter(e => e.exam_date && e.subject.trim());
  const canSave  = classId && examName.trim() && validEntries.length > 0;
  const draft    = { exam_name: examName.trim(), start_date: startDate || null, end_date: endDate || null, entries: validEntries };
  const chosen   = roster ? roster.students.filter(s => selected.has(s.id)) : [];
  const canPrint = chosen.length > 0 && canSave;

  async function handleSave() {
    setSaving(true); setMsg('');
    try {
      const result = await saveDatesheet({ class_id: +classId, ...draft });
      setDatesheets(await getDatesheets(classId));
      setDatesheetId(String(result.id));
      setMsg('Datesheet saved.');
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Admit Card Generator</h2>

      <Card className="p-4 mb-4">
        <CardHeader title="1. Exam datesheet" />
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Class
            <select className={inputCls} value={classId} onChange={e => setClassId(e.target.value)}>
              {!classes.length && <option value="">No classes yet</option>}
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Saved datesheet
            <select className={inputCls} value={datesheetId} onChange={e => pickDatesheet(e.target.value)}>
              <option value="">+ New datesheet</option>
              {datesheets.map(d => <option key={d.id} value={d.id}>{d.exam_name}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Exam name
            <input type="text" className={inputCls} placeholder="e.g. Half Yearly Examination"
              value={examName} onChange={e => setExamName(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Start date
            <input type="date" className={inputCls} value={startDate} onChange={e => setStartDate(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">End date
            <input type="date" className={inputCls} value={endDate} onChange={e => setEndDate(e.target.value)} />
          </label>
        </div>

        <table className="w-full text-sm border-collapse mb-3">
          <thead>
            <tr className="text-left text-xs font-bold text-ink-soft">
              <th className="pb-2 pr-2">Date</th>
              <th className="pb-2 pr-2">Subject</th>
              <th className="pb-2 pr-2">Start time</th>
              <th className="pb-2 pr-2">End time</th>
              <th className="pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={i} className="border-t border-line-soft">
                <td className="py-1.5 pr-2"><input type="date" className={inputCls} value={e.exam_date} onChange={ev => updateEntry(i, 'exam_date', ev.target.value)} /></td>
                <td className="py-1.5 pr-2"><input type="text" className={inputCls} placeholder="Subject" value={e.subject} onChange={ev => updateEntry(i, 'subject', ev.target.value)} /></td>
                <td className="py-1.5 pr-2"><input type="time" className={inputCls} value={e.start_time} onChange={ev => updateEntry(i, 'start_time', ev.target.value)} /></td>
                <td className="py-1.5 pr-2"><input type="time" className={inputCls} value={e.end_time} onChange={ev => updateEntry(i, 'end_time', ev.target.value)} /></td>
                <td className="py-1.5">
                  <button onClick={() => removeEntry(i)} className="text-critical p-1.5 rounded-s hover:bg-critical-tint" aria-label="Remove day">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex items-center gap-3">
          <Button variant="secondary" className="flex items-center gap-1.5" onClick={addEntry}>
            <Plus size={14} /> Add day
          </Button>
          <Button className="flex items-center gap-1.5" disabled={!canSave || saving} onClick={handleSave}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save datesheet'}
          </Button>
        </div>
      </Card>

      {msg && <p className={`text-sm font-bold mb-3 ${msg.startsWith('Error') ? 'text-critical' : 'text-transit-deep'}`}>{msg}</p>}

      <Card className="p-4 mb-4">
        <CardHeader title="2. Generate & print" action={
          <Button className="flex items-center gap-1.5" disabled={!canPrint} onClick={() => setPrint(true)}>
            <Printer size={14} /> Print {chosen.length} admit card{chosen.length === 1 ? '' : 's'}
          </Button>
        } />

        {!canSave && (
          <p className="flex items-center gap-2 text-sm font-semibold text-caution bg-caution-tint px-3 py-2 rounded-s w-fit mb-3 mt-3">
            <AlertTriangle size={15} /> Enter an exam name and at least one day with a subject above to enable printing.
          </p>
        )}

        {roster && (
          <div className="grid grid-cols-[minmax(260px,320px)_1fr] gap-5 items-start max-[900px]:grid-cols-1 mt-3">
            <Card>
              <CardHeader title={`Students (${selected.size}/${roster.students.length})`} action={
                <div className="flex gap-2 text-xs font-bold">
                  <button className="text-transit-deep hover:underline" onClick={() => setSelected(new Set(roster.students.map(s => s.id)))}>All</button>
                  <button className="text-transit-deep hover:underline" onClick={() => setSelected(new Set())}>None</button>
                </div>
              } />
              {!roster.students.length && <p className="px-5 py-6 text-sm text-ink-soft">No students in this class.</p>}
              {roster.students.map(s => (
                <div key={s.id} className="flex items-center gap-3 px-5 py-2.5 border-t border-line-soft first:border-t-0">
                  <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} aria-label={`Include ${s.name}`}
                    className="w-4 h-4 accent-[var(--transit)]" />
                  <button onClick={() => onOpenStudent(s.id)} className="text-sm font-semibold text-transit-deep hover:underline text-left flex-1 min-w-0 truncate">
                    {s.name}
                  </button>
                </div>
              ))}
            </Card>

            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-soft mb-2">
                Preview {chosen.length > 1 ? `(1 of ${chosen.length} — each prints on its own page)` : ''}
              </h3>
              {chosen.length
                ? <AdmitCard student={chosen[0]} school={roster.school} datesheet={draft} />
                : <p className="text-sm text-ink-soft">Tick students on the left to preview their admit card.</p>}
            </div>
          </div>
        )}
      </Card>

      {print && <AdmitCardSheet students={chosen} school={roster.school} datesheet={draft} onClose={() => setPrint(false)} />}
    </div>
  );
}
